import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { installerApi, uploadViaSignedUrl } from '../../utils/installerApi';
import { useInstallerSession } from '../../hooks/useInstallerSession';
import { reverseGeocode } from '../../utils/reverseGeocode';
import MapAddressPicker from './MapAddressPicker';
import { InstallerTopbar } from './InstallerChrome';
import { currentInstaller, listStageOutbox, listQueued } from '../../installer/pwa/offlineQueue';
import { listDrafts } from '../../installer/pwa/stageDrafts';
import '../../styles/InstallerPages.css';

const DAYS = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'];
const toFa = (s) => String(s)
  .replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
  .replace(/[٠-٩]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d.charCodeAt(0) - 0x0660]);
const TIME_OPTS = [];
for (let h = 0; h < 24; h += 1) for (const m of [0, 30]) TIME_OPTS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);

function faError(e) {
  const m = (e && e.message) || String(e);
  if (/failed to fetch|load failed|networkerror|err_network|connection/i.test(m)) {
    return 'ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید و دوباره تلاش کنید.';
  }
  return m;
}

export default function InstallerProfilePage() {
  const nav = useNavigate();
  const { token, logout } = useInstallerSession();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [unsentCount, setUnsentCount] = useState(null);
  const [addrLoading, setAddrLoading] = useState(false);
  const revSeq = useRef(0);

  useEffect(() => {
    if (!token) { nav('/installer/login'); return undefined; }
    let cancelled = false;
    installerApi.profile(token).then((r) => {
      if (cancelled) return;
      setData(r);
      setForm({
        full_name: r.full_name || '', base_address_text: r.base_address_text || '',
        loc: (r.base_lat != null && r.base_lng != null) ? { lat: r.base_lat, lng: r.base_lng } : null,
        vehicle_type: r.vehicle_type || 'motorcycle',
        work_days: r.work_hours_days || [], work_start: r.work_hours_start || '08:00', work_end: r.work_hours_end || '18:00',
      });
    }).catch((e) => setErr(faError(e)));
    return () => { cancelled = true; };
  }, [token, nav]);

  const locLat = form?.loc?.lat ?? null;
  const locLng = form?.loc?.lng ?? null;
  useEffect(() => {
    if (locLat == null || locLng == null) return undefined;
    const seq = ++revSeq.current;
    const timer = setTimeout(async () => {
      setAddrLoading(true);
      try {
        const addr = await reverseGeocode(locLat, locLng);
        if (seq === revSeq.current && addr) setForm((f) => ({ ...f, base_address_text: toFa(addr) }));
      } catch { /* keep the field editable on failure */ }
      finally { if (seq === revSeq.current) setAddrLoading(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [locLat, locLng]);

  const toggleDay = (i) => setForm((f) => ({
    ...f, work_days: f.work_days.includes(i) ? f.work_days.filter((x) => x !== i) : [...f.work_days, i],
  }));

  const save = async () => {
    setBusy(true); setErr(null); setOk(false);
    try {
      if (!form.full_name.trim()) throw new Error('نام نمی‌تواند خالی باشد.');
      if (!form.loc) throw new Error('موقعیت پایگاه را روی نقشه مشخص کنید.');
      let photoPath;
      if (photoFile) photoPath = await uploadViaSignedUrl('profile_photo', photoFile, { session_token: token }, 'image');
      await installerApi.updateProfile(token, {
        full_name: form.full_name, base_address_text: form.base_address_text,
        base_lat: form.loc.lat, base_lng: form.loc.lng, vehicle_type: form.vehicle_type,
        work_hours_days: form.work_days, work_hours_start: form.work_start, work_hours_end: form.work_end,
        ...(photoPath ? { profile_photo_path: photoPath } : {}),
      });
      setOk(true); setPhotoFile(null);
    } catch (e) { setErr(faError(e)); } finally { setBusy(false); }
  };

  const performLogout = async () => {
    setBusy(true); setErr(null);
    try {
      if (localStorage.getItem('installer_session_token') !== token) throw new Error('نشست نیروی اجرایی تغییر کرد. دوباره وارد شوید.');
      try { await installerApi.logout(token); } catch { /* best-effort */ }
      await logout(); nav('/installer/login');
    } catch (e) { setErr(faError(e)); }
    finally { setBusy(false); }
  };
  const doLogout = async () => {
    setBusy(true); setErr(null);
    try {
      const installerId = currentInstaller(token);
      if (!installerId) throw new Error('نشست نیروی اجرایی تغییر کرد. دوباره وارد شوید.');
      const [outbox, drafts, legacy] = await Promise.all([listStageOutbox(installerId), listDrafts(installerId), listQueued(installerId)]);
      if (currentInstaller(token) !== installerId) throw new Error('نشست نیروی اجرایی تغییر کرد. دوباره وارد شوید.');
      // A draft and its frozen outbox copy are one unsent submission, not two.
      const stages = new Set([...outbox, ...drafts.filter((d) => d.photos?.length)].map((d) => d.id));
      const count = stages.size + legacy.length;
      if (count) setUnsentCount(count);
      else await performLogout();
    } catch { setErr('مدارک ذخیره‌شده بررسی نشد. برای خروج دوباره تلاش کنید.'); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true); setErr(null);
    try { await installerApi.deleteAccount(token); await logout(); nav('/installer/login'); }
    catch (e) { setErr(faError(e)); setBusy(false); }
  };

  const back = (
    <button type="button" className="inst-icon-btn" onClick={() => nav('/installer/dashboard')} aria-label="بازگشت">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
    </button>
  );

  return (
    <div className="inst">
      <div className="inst-shell">
        <InstallerTopbar sub="پروفایل من" action={back} />
        <div className="inst-card">
          {err && <div role="alert" className="inst-alert">{err}</div>}
          {ok && <div className="inst-ok">تغییرات ذخیره شد.</div>}
          {!form ? <p className="inst-readonly">در حال بارگذاری...</p> : (
            <>
              <div className="inst-field">
                <label className="inst-label" htmlFor="pname">نام و نام خانوادگی</label>
                <input className="inst-input" id="pname" value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </div>

              <div className="inst-field">
                <label className="inst-label" htmlFor="pphoto">عکس پروفایل</label>
                {data.profile_photo_url && <img className="inst-photo-preview" src={data.profile_photo_url} alt="عکس پروفایل" />}
                <input className="inst-file" id="pphoto" type="file" accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files[0])} />
              </div>

              <div className="inst-field">
                <label className="inst-label" htmlFor="paddr">آدرس خانه/پایگاه</label>
                <input className="inst-input" id="paddr" value={form.base_address_text}
                  onChange={(e) => setForm({ ...form, base_address_text: toFa(e.target.value) })} placeholder="آدرس متنی" />
                <div className="inst-map"><MapAddressPicker value={form.loc} onChange={(loc) => setForm({ ...form, loc })} height={220} /></div>
                <div className="inst-map-hint">{addrLoading ? 'در حال یافتن آدرس...' : 'برای تغییر پایگاه، نقطهٔ جدید را روی نقشه لمس کنید.'}</div>
              </div>

              <div className="inst-field">
                <label className="inst-label" htmlFor="pvehicle">نوع وسیله نقلیه</label>
                <select className="inst-select" id="pvehicle" value={form.vehicle_type}
                  onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}>
                  <option value="car">ماشین</option>
                  <option value="motorcycle">موتور</option>
                  <option value="foot">پیاده</option>
                </select>
              </div>

              <fieldset className="inst-fieldset">
                <legend className="inst-legend">ساعات کاری</legend>
                <div className="inst-daypills">
                  {DAYS.map((d, i) => (
                    <button type="button" key={d} aria-pressed={form.work_days.includes(i)}
                      className={`inst-daypill ${form.work_days.includes(i) ? 'is-on' : ''}`}
                      onClick={() => toggleDay(i)}>{d}</button>
                  ))}
                </div>
                <div className="inst-timerow">
                  <select className="inst-select" aria-label="ساعت شروع" style={{ flex: 1 }} value={form.work_start}
                    onChange={(e) => setForm({ ...form, work_start: e.target.value })}>
                    {TIME_OPTS.map((t) => <option key={t} value={t}>{toFa(t)}</option>)}
                  </select>
                  <span className="inst-timesep">تا</span>
                  <select className="inst-select" aria-label="ساعت پایان" style={{ flex: 1 }} value={form.work_end}
                    onChange={(e) => setForm({ ...form, work_end: e.target.value })}>
                    {TIME_OPTS.map((t) => <option key={t} value={t}>{toFa(t)}</option>)}
                  </select>
                </div>
              </fieldset>

              <button className="inst-btn" disabled={busy} onClick={save}>
                {busy && <span className="inst-spin" />}ذخیره تغییرات
              </button>

              <p className="inst-readonly">شماره موبایل: {toFa(data.phone)} (غیرقابل تغییر)</p>
              <p className="inst-readonly">کارت ملی: {data.has_national_id ? 'روی پرونده ثبت شده' : 'ثبت نشده'} · رزومه: {data.has_resume ? 'روی پرونده ثبت شده' : 'ثبت نشده'}</p>

              <div className="inst-divider" />
              <button type="button" className="inst-btn inst-btn-ghost" disabled={busy} onClick={doLogout}>خروج از حساب</button>
              {unsentCount != null && <section className="inst-alert" role="alert" aria-label="مدارک ارسال‌نشده">
                <p>{toFa(unsentCount)} ارسال هنوز به سامانه نرسیده است و با خروج پاک می‌شود.</p>
                <div className="inst-btnrow">
                  <button type="button" className="inst-btn inst-btn-ghost" autoFocus disabled={busy} onClick={() => setUnsentCount(null)}>ماندن و نگه‌داشتن مدارک</button>
                  <button type="button" className="inst-btn inst-btn-danger" disabled={busy} onClick={performLogout}>پاک‌کردن مدارک و خروج</button>
                </div>
              </section>}

              <div className="inst-danger-zone">
                {!confirmDel ? (
                  <button type="button" className="inst-btn inst-btn-danger" onClick={() => setConfirmDel(true)}>حذف حساب</button>
                ) : (
                  <>
                    <p className="inst-danger-text">حساب شما غیرفعال می‌شود و دیگر امکانِ ورود ندارید. این عمل بازگشت‌ناپذیر است.</p>
                    <div className="inst-btnrow">
                      <button type="button" className="inst-btn inst-btn-ghost" disabled={busy} onClick={() => setConfirmDel(false)}>انصراف</button>
                      <button type="button" className="inst-btn inst-btn-danger" disabled={busy} onClick={doDelete}>
                        {busy && <span className="inst-spin" />}بله، حساب را حذف کن
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
